import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { useMemo, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	compareRollsetNames,
	rollsOf,
	rollsetSearchRank,
} from "../models/Rollshop";
import type { RollsetStatusOption } from "../repositories/RollshopOperationsRepository";

const ALL_OPTION: RollsetStatusOption = { Code: "", Name: "All" };

// Bold on every place the typed text appears, as the legacy typeahead does.
const Highlighted = ({ text, search }: { text: string; search: string }) => {
	const needle = search.trim().toLowerCase();
	if (!needle) return <>{text}</>;
	const parts: { text: string; match: boolean }[] = [];
	const lower = text.toLowerCase();
	let from = 0;
	for (
		let at = lower.indexOf(needle);
		at !== -1;
		at = lower.indexOf(needle, from)
	) {
		if (at > from) parts.push({ text: text.slice(from, at), match: false });
		parts.push({ text: text.slice(at, at + needle.length), match: true });
		from = at + needle.length;
	}
	if (from < text.length) parts.push({ text: text.slice(from), match: false });
	return (
		<>
			{parts.map((part, i) =>
				part.match ? (
					<Box
						// biome-ignore lint/suspicious/noArrayIndexKey: parts never reorder
						key={i}
						component="span"
						sx={{ fontWeight: 700, color: "text.primary" }}
					>
						{part.text}
					</Box>
				) : (
					// biome-ignore lint/suspicious/noArrayIndexKey: parts never reorder
					<span key={i}>{part.text}</span>
				),
			)}
		</>
	);
};

interface Props {
	rollsets: RollsetListItem[];
	/** The rollset selected on the page: the search box shows it. */
	pickedRollset: RollsetListItem | null;
	onRollsetPicked: (row: RollsetListItem) => void;
	statusCode: string | null;
	onStatusChange: (value: string | null) => void;
	statusOptions: RollsetStatusOption[];
	onReset: () => void;
	canReset: boolean;
}

export const RollshopToolbar = ({
	rollsets,
	pickedRollset,
	onRollsetPicked,
	statusCode,
	onStatusChange,
	statusOptions,
	onReset,
	canReset,
}: Props) => {
	const [searchText, setSearchText] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const options = [ALL_OPTION, ...statusOptions];
	const selectedStatus =
		options.find((o) => o.Code === statusCode) ?? ALL_OPTION;

	// Legacy searches within the Op. Status filter, so a pick is always on the list.
	const matches = useMemo(
		() =>
			rollsets
				.map((row) => ({ row, rank: rollsetSearchRank(row, searchText) }))
				.filter(
					(m): m is { row: RollsetListItem; rank: number } => m.rank !== null,
				)
				.sort(
					(a, b) =>
						a.rank - b.rank ||
						compareRollsetNames(a.row.Name ?? "", b.row.Name ?? ""),
				)
				.map((m) => m.row),
		[rollsets, searchText],
	);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				flexWrap: "wrap",
				px: 1.5,
				py: 0.75,
			}}
		>
			<Autocomplete<RollsetListItem>
				options={isTyping ? matches : pickedRollset ? [pickedRollset] : []}
				// While typing there is no value, so the first match is the highlighted one.
				value={isTyping ? null : pickedRollset}
				inputValue={isTyping ? searchText : (pickedRollset?.Name ?? "")}
				onInputChange={(_event, value, reason) => {
					// The clear button empties the box for a new search; the selection stays.
					if (reason !== "input" && reason !== "clear") return;
					setSearchText(reason === "clear" ? "" : value);
					setIsTyping(true);
				}}
				onChange={(_event, row) => {
					if (!row) return;
					setIsTyping(false);
					onRollsetPicked(row);
				}}
				onBlur={() => setIsTyping(false)}
				open={searchOpen && isTyping && searchText.trim() !== ""}
				onOpen={() => setSearchOpen(true)}
				onClose={() => setSearchOpen(false)}
				filterOptions={(x) => x}
				getOptionLabel={(row) => row.Name ?? ""}
				isOptionEqualToValue={(option, current) => option.Id === current.Id}
				noOptionsText="No rollsets"
				size="small"
				autoHighlight
				clearOnBlur={false}
				blurOnSelect
				popupIcon={null}
				renderOption={(props, row) => {
					const { key, ...rest } = props as typeof props & { key: string };
					// The rollset and its rolls, so a match on a roll shows where it is.
					return (
						<li key={row.Id} {...rest}>
							<Box sx={{ minWidth: 0 }}>
								<Typography noWrap sx={{ fontSize: 13, lineHeight: 1.4 }}>
									<Highlighted text={row.Name ?? ""} search={searchText} />
								</Typography>
								<Typography
									noWrap
									sx={{
										fontSize: 12,
										lineHeight: 1.3,
										color: "text.secondary",
									}}
								>
									<Highlighted text={rollsOf(row)} search={searchText} />
								</Typography>
							</Box>
						</li>
					);
				}}
				slotProps={{ popper: { sx: { minWidth: 440 } } }}
				sx={{ width: 240 }}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Search rollset"
						placeholder="Rollset or roll"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				)}
			/>
			<Autocomplete
				options={options}
				value={selectedStatus}
				size="small"
				disableClearable
				autoHighlight
				getOptionLabel={(option) => option.Name}
				isOptionEqualToValue={(option, current) => option.Code === current.Code}
				onChange={(_event, option) => onStatusChange(option.Code || null)}
				renderOption={(props, option) => {
					const { key, ...rest } = props as typeof props & { key: string };
					return (
						<li key={option.Code || "all"} {...rest}>
							{option.Name}
						</li>
					);
				}}
				sx={{ width: 190 }}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Op. Status"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				)}
			/>
			<ResetFiltersButton onClick={onReset} disabled={!canReset} />
		</Box>
	);
};
